import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
} from '@angular/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

import {
  ISidenavContainer,
  SIDENAV_CONTAINER,
} from '../sidenav-container.token';
import { SidenavComponent } from '../sidenav/sidenav.component';

@Component({
  selector: 'app-sidenav-container',
  templateUrl: './sidenav-container.component.html',
  styleUrls: ['./sidenav-container.component.scss'],
  host: {
    '[class]': 'classes',
    '(document:keydown.escape)': 'onEscapeKey()',
  },
  providers: [
    { provide: SIDENAV_CONTAINER, useExisting: SidenavContainerComponent },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidenavContainerComponent implements ISidenavContainer, OnDestroy {
  @Input()
  set hasBackdrop(value: boolean | string) {
    this._hasBackdrop = coerceBooleanProperty(value);
  }
  get hasBackdrop(): boolean {
    return this._hasBackdrop;
  }

  @Output() backdropClick = new EventEmitter<void>();

  contentMarginLeft: number | null = null;
  showBackdrop = false;

  private _sidenavs: SidenavComponent[] = [];
  private _hasBackdrop = true;

  constructor(
    private _ngZone: NgZone,
    private _cdr: ChangeDetectorRef
  ) {}

  get classes(): string {
    return 'sidenav-container';
  }

  registerSidenav(sidenav: SidenavComponent): void {
    this._sidenavs.push(sidenav);
    this.updateLayout();
  }

  deregisterSidenav(sidenav: SidenavComponent): void {
    const index = this._sidenavs.indexOf(sidenav);
    if (index > -1) {
      this._sidenavs.splice(index, 1);
      this.updateLayout();
    }
  }

  updateLayout(): void {
    this._updateBackdrop();

    // Defer margin measurement to next frame so the DOM reflects the new state
    this._ngZone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        this._ngZone.run(() => {
          this._updateContentMargin();
          this._cdr.markForCheck();
        });
      });
    });
  }

  onBackdropClick(): void {
    this.backdropClick.emit();

    this._sidenavs.forEach((sidenav) => {
      if (sidenav.opened && sidenav.mode === 'over' && !sidenav.disableClose) {
        sidenav.close();
      }
    });
  }

  onEscapeKey(): void {
    this._sidenavs.forEach((sidenav) => {
      if (sidenav.opened && sidenav.mode === 'over' && !sidenav.disableClose) {
        sidenav.close();
      }
    });
  }

  ngOnDestroy(): void {
    this._sidenavs = [];
  }

  private _updateBackdrop(): void {
    if (!this._hasBackdrop) {
      this.showBackdrop = false;
      return;
    }
    this.showBackdrop = this._sidenavs.some(
      (sidenav) => sidenav.opened && sidenav.mode === 'over'
    );
  }

  private _updateContentMargin(): void {
    let left = 0;

    this._sidenavs.forEach((sidenav) => {
      if (sidenav.opened && sidenav.mode === 'side') {
        left += sidenav._getWidth();
      }
    });

    this.contentMarginLeft = left || null;
  }
}
